import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, Loader2, AlertTriangle, Clock, Users, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useLanguage } from "@/i18n/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SubscriptionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, isRTL } = useLanguage();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [pricingPlans, setPricingPlans] = useState(null);
  
  // Check if this is a new employer redirect
  const isNewEmployer = location.state?.isNewEmployer || false;

  useEffect(() => {
    // Fetch subscription status
    fetch(API_URL + "/api/subscription/status", { credentials: "include" })
      .then(function(res) { return res.ok ? res.json() : null; })
      .then(function(data) { setStatus(data); })
      .finally(function() { setLoading(false); });
    
    // Fetch pricing plans
    fetch(API_URL + "/api/pricing")
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setPricingPlans(data.plans); });
  }, []);

  const getPlanPrice = (planId) => {
    if (!pricingPlans || !pricingPlans[planId]) return null;
    return pricingPlans[planId].price;
  };

  const getPlanEmployees = (planId) => {
    if (!pricingPlans || !pricingPlans[planId]) return null;
    return pricingPlans[planId].max_employees;
  };

  const handleSubscribe = async function(planId) {
    setCheckoutLoading(true);
    try {
      const res = await fetch(API_URL + "/api/subscription/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ plan: planId, origin_url: window.location.origin })
      });
      if (res.ok) {
        const data = await res.json();
        window.location.href = data.checkout_url;
      } else {
        toast.error("Failed to start checkout");
      }
    } catch (e) {
      toast.error("Failed");
    }
    setCheckoutLoading(false);
  };

  const handleCancel = async function() {
    const res = await fetch(API_URL + "/api/subscription/cancel", { method: "POST", credentials: "include" });
    if (res.ok) {
      toast.success("Canceled");
      setShowCancelDialog(false);
      const r = await fetch(API_URL + "/api/subscription/status", { credentials: "include" });
      if (r.ok) setStatus(await r.json());
    } else {
      toast.error("Failed");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  const isTrial = status && status.status === "trial";
  const isActive = status && status.status === "active";
  const isExpired = status && (status.status === "expired" || status.status === "canceled" || status.status === "payment_failed");

  return (
    <div className={`min-h-screen bg-background p-8 ${isRTL ? 'rtl' : 'ltr'}`}>
      <div className="max-w-5xl mx-auto">
        
        {/* Welcome banner for new employers */}
        {isNewEmployer && (
          <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-primary/10 to-primary/5 border-2 border-primary/20">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary/20 flex items-center justify-center">
                <Sparkles className="w-7 h-7 text-primary" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-primary">{t("welcomeNewEmployer") || "Welcome to GeoCheck!"}</h2>
                <p className="text-primary/80">{t("welcomeNewEmployerDesc") || "Your company has been created. Choose a plan to unlock all features, or continue with your 10-day free trial."}</p>
              </div>
              <Button 
                variant="outline" 
                className="shrink-0"
                onClick={() => navigate('/employer')}
                data-testid="skip-subscription-btn"
              >
                {t("skipForNow") || "Skip for Now"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
        
        {isTrial && (
          <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-yellow-50 to-orange-50 border-2 border-yellow-200">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="w-7 h-7 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-yellow-800">{t("freeTrialActive") || "Free Trial Active"}</h2>
                <p className="text-yellow-700">{status.days_left} {t("days") || "days"} {t("remaining") || "left"}</p>
                <p className="text-sm text-yellow-600">{status.employee_count || 0} / {status.max_employees || 5} {t("employees") || "employees"}</p>
              </div>
            </div>
          </div>
        )}

        {isActive && (
          <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="w-7 h-7 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-green-800">{t("active") || "Active"}: {status.plan}</h2>
                <p className="text-green-700">{status.employee_count || 0} / {status.max_employees || 20} {t("employees") || "employees"}</p>
              </div>
            </div>
            <Button variant="outline" className="text-red-600 border-red-200" onClick={function() { setShowCancelDialog(true); }}>
              {t("cancelSubscription") || "Cancel"}
            </Button>
          </div>
        )}

        {isExpired && (
          <div className="mb-8 p-6 rounded-2xl bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-200">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-7 h-7 text-red-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-red-800">
                  {status.status === "payment_failed" ? "Payment Failed" : status.status === "canceled" ? t("subscriptionCancelledSuccess") : "Expired"}
                </h2>
                <p className="text-red-700">{t("upgradeNow") || "Subscribe below to restore access"}</p>
              </div>
            </div>
          </div>
        )}

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">{t("pricingTitle") || "Choose Your Plan"}</h1>
          <p className="text-muted-foreground">{t("pricingSubtitle") || "Simple pricing based on team size"}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card className="relative">
            <CardHeader>
              <CardTitle>{pricingPlans?.growth?.name || t("planGrowth") || "Growth"}</CardTitle>
              <div className="mt-4">
                <span className="text-4xl font-bold">{getPlanPrice("growth") || "180"}</span>
                <span className="text-sm font-normal text-muted-foreground"> SAR/{t("month") || "mo"}</span>
              </div>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-2">
                <Users className="w-4 h-4" /> {t("upTo") || "Up to"} {getPlanEmployees("growth") || 20} {t("employees") || "employees"}
              </p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-green-500" /> {t("allFeaturesIncluded") || "All features"}</li>
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-green-500" /> {t("planGrowthFeature1") || "Email support"}</li>
              </ul>
              {status && status.plan === "growth" && status.status === "active" ? (
                <Button className="w-full" disabled variant="outline">{t("currentPlan") || "Current Plan"}</Button>
              ) : (
                <Button onClick={function() { handleSubscribe("growth"); }} className="w-full" variant="outline" disabled={checkoutLoading}>
                  {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("getStarted") || "Subscribe"}
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="relative border-primary shadow-lg ring-2 ring-primary">
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary">{t("mostUsed") || "Popular"}</Badge>
            {status && status.plan === "business" && status.status === "active" && <Badge className="absolute -top-3 right-4 bg-green-500">{t("currentPlan") || "Current"}</Badge>}
            <CardHeader>
              <CardTitle>{pricingPlans?.business?.name || t("planBusiness") || "Business"}</CardTitle>
              <div className="mt-4">
                <span className="text-4xl font-bold">{getPlanPrice("business") || "360"}</span>
                <span className="text-sm font-normal text-muted-foreground"> SAR/{t("month") || "mo"}</span>
              </div>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-2">
                <Users className="w-4 h-4" /> {t("upTo") || "Up to"} {getPlanEmployees("business") || 60} {t("employees") || "employees"}
              </p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-green-500" /> {t("allFeaturesIncluded") || "All features"}</li>
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-green-500" /> {t("planBusinessFeature1") || "Priority support"}</li>
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-green-500" /> {t("planBusinessFeature2") || "Unlimited admins"}</li>
              </ul>
              {status && status.plan === "business" && status.status === "active" ? (
                <Button className="w-full" disabled variant="outline">{t("currentPlan") || "Current Plan"}</Button>
              ) : (
                <Button onClick={function() { handleSubscribe("business"); }} className="w-full" disabled={checkoutLoading}>
                  {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("getStarted") || "Subscribe"}
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="relative">
            {status && status.plan === "professional" && status.status === "active" && <Badge className="absolute -top-3 right-4 bg-green-500">{t("currentPlan") || "Current"}</Badge>}
            <CardHeader>
              <CardTitle>{pricingPlans?.professional?.name || t("planProfessional") || "Professional"}</CardTitle>
              <div className="mt-4">
                <span className="text-4xl font-bold">{getPlanPrice("professional") || "500"}</span>
                <span className="text-sm font-normal text-muted-foreground"> SAR/{t("month") || "mo"}</span>
              </div>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-2">
                <Users className="w-4 h-4" /> {t("upTo") || "Up to"} {getPlanEmployees("professional") || 100} {t("employees") || "employees"}
              </p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-green-500" /> {t("allFeaturesIncluded") || "All features"}</li>
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-green-500" /> {t("planProfessionalFeature1") || "Priority support"}</li>
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-green-500" /> {t("planProfessionalFeature2") || "Advanced analytics"}</li>
              </ul>
              {status && status.plan === "professional" && status.status === "active" ? (
                <Button className="w-full" disabled variant="outline">{t("currentPlan") || "Current Plan"}</Button>
              ) : (
                <Button onClick={function() { handleSubscribe("professional"); }} className="w-full" variant="outline" disabled={checkoutLoading}>
                  {checkoutLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t("getStarted") || "Subscribe"}
                </Button>
              )}
            </CardContent>
          </Card>

          <Card className="relative">
            {status && status.plan === "enterprise" && status.status === "active" && <Badge className="absolute -top-3 right-4 bg-green-500">{t("currentPlan") || "Current"}</Badge>}
            <CardHeader>
              <CardTitle>{pricingPlans?.enterprise?.name || t("planEnterprise") || "Enterprise"}</CardTitle>
              <div className="mt-4">
                <span className="text-2xl font-bold">{t("contactUs") || "Contact Us"}</span>
              </div>
              <p className="text-sm text-muted-foreground flex items-center gap-1 mt-2">
                <Users className="w-4 h-4" /> {t("planEnterpriseTeam") || "Unlimited employees"}
              </p>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 mb-6">
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-green-500" /> {t("allFeaturesIncluded") || "All features"}</li>
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-green-500" /> {t("planEnterpriseFeature1") || "Dedicated manager"}</li>
                <li className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-green-500" /> {t("planEnterpriseFeature2") || "Custom integrations"}</li>
              </ul>
              <Button className="w-full" variant="outline" onClick={function() { navigate("/contact"); }}>
                {t("contactUs") || "Contact Sales"}
              </Button>
            </CardContent>
          </Card>
        </div>

        <div className="text-center">
          <Button variant="outline" onClick={function() { navigate('/employer'); }}>{t("goToDashboard") || "Go to Dashboard"}</Button>
        </div>

        <AlertDialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Cancel Subscription?</AlertDialogTitle>
              <AlertDialogDescription>Your access will be restricted immediately.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep</AlertDialogCancel>
              <AlertDialogAction onClick={handleCancel} className="bg-destructive">Cancel</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

export function PaywallPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);

  useEffect(function() {
    const check = async function() {
      const res = await fetch(API_URL + "/api/subscription/status", { credentials: "include" });
      if (res.ok) {
        const d = await res.json();
        setStatus(d);
        if (d.has_access) navigate("/employer");
      }
    };
    check();
    const interval = setInterval(check, 5000);
    return function() { clearInterval(interval); };
  }, [navigate]);

  const logout = async function() {
    await fetch(API_URL + "/api/auth/logout", { method: "POST", credentials: "include" }).catch(function() {});
    navigate("/");
  };

  let title = "Trial Expired";
  if (status && status.status === "canceled") title = "Subscription Canceled";
  if (status && status.status === "payment_failed") title = "Payment Failed";

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <Card className="max-w-lg w-full">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
            <AlertTriangle className="w-10 h-10 text-yellow-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">{title}</h1>
          <p className="text-muted-foreground mb-6">Subscribe to continue using GeoCheck</p>
          <div className="space-y-3">
            <Button onClick={function() { navigate("/subscription"); }} className="w-full" size="lg">View Plans</Button>
            <Button variant="outline" onClick={function() { navigate("/"); }} className="w-full">Home</Button>
            <Button variant="ghost" onClick={logout} className="w-full text-muted-foreground">Log Out</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function SubscriptionSuccessPage() {
  const navigate = useNavigate();
  
  useEffect(function() {
    const timer = setTimeout(function() {
      navigate("/employer");
    }, 3000);
    return function() { clearTimeout(timer); };
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-8">
      <Card className="max-w-lg w-full">
        <CardContent className="p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <Check className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Payment Successful!</h1>
          <p className="text-muted-foreground mb-6">Your subscription is now active. Redirecting to dashboard...</p>
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </CardContent>
      </Card>
    </div>
  );
}

export default SubscriptionPage;
